/**
 * WMS overlays on the map (design §9.2): which are shown, how opaque, in
 * what order. Per browser, as a convenience.
 *
 * @module
 */
import type { Map as MapLibre } from "maplibre-gl";
import type { Schemas } from "../api/client";
import { abs } from "./style";
import { escapeHtml } from "../trustedTypes";

export type WmsOverlay = Schemas["WmsOverlay"];

export interface OverlayState {
  /** Bottom first. */
  order: string[];
  shown: Record<string, boolean>;
  opacity: Record<string, number>;
}

const KEY = "avgv.overlays";

export function loadOverlayState(): OverlayState {
  try {
    const s = JSON.parse(
      localStorage.getItem(KEY) ?? "null",
    ) as OverlayState | null;
    if (s && Array.isArray(s.order)) return s;
  } catch {
    // A missing or unreadable setting just means the defaults.
  }
  return { order: [], shown: {}, opacity: {} };
}

export function saveOverlayState(s: OverlayState) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Remembering the overlays is a convenience.
  }
}

/** The overlays in drawing order: remembered ones first, new ones on top. */
export function ordered(
  all: readonly WmsOverlay[],
  s: OverlayState,
): WmsOverlay[] {
  const byId = new Map(all.map((o) => [o.id, o]));
  const known = s.order.flatMap((id) => {
    const o = byId.get(id);
    return o ? [o] : [];
  });
  return [...known, ...all.filter((o) => !s.order.includes(o.id))];
}

/** Moves `id` one step up (towards the top) or down. */
export function move(
  s: OverlayState,
  ids: string[],
  id: string,
  up: boolean,
): OverlayState {
  const order = [...ids];
  const i = order.indexOf(id);
  const j = up ? i + 1 : i - 1;
  if (i < 0 || j < 0 || j >= order.length) return s;
  [order[i], order[j]] = [order[j] as string, order[i] as string];
  return { ...s, order };
}

const layerId = (id: string) => `wms-${id}`;

/**
 * Adds or updates the overlays' sources and layers below `beforeId` (the
 * tracks), in order; a style change removes them, so this runs after each.
 */
export function applyOverlays(
  map: MapLibre,
  all: readonly WmsOverlay[],
  s: OverlayState,
  beforeId: string,
) {
  for (const o of ordered(all, s)) {
    const id = layerId(o.id);
    if (!map.getSource(id)) {
      map.addSource(id, {
        type: "raster",
        tiles: [abs(`/api/wms/${o.id}/tiles/{z}/{x}/{y}`)],
        tileSize: 256,
        maxzoom: o.max_zoom,
        // Third-party text, shown as text (MapLibre takes HTML).
        attribution: `Overlay (third-party): ${escapeHtml(o.attribution ?? o.name)}`,
      });
    }
    if (!map.getLayer(id)) {
      map.addLayer(
        { id, type: "raster", source: id },
        map.getLayer(beforeId) ? beforeId : undefined,
      );
    } else if (map.getLayer(beforeId)) {
      map.moveLayer(id, beforeId);
    }
    map.setLayoutProperty(id, "visibility", s.shown[o.id] ? "visible" : "none");
    map.setPaintProperty(id, "raster-opacity", s.opacity[o.id] ?? 0.7);
  }
}
